<script setup lang="ts">
import {
    Button,
    type DecisionView,
    type SettledOutcome,
    ZhAuthLayout,
    ZhBanner,
    ZhDecisionCard,
    ZhPageTitle,
    ZhPanel,
    ZhStatusPill,
    ZhSystemLabel,
} from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { formatDuration, type GateLinkGate, type GateLinkView, gateLinkApi, waitedMs } from '@/api'
import { gateDecisionSections } from '@/lib/gateSummary'
import { toolLabel } from '@/lib/presentation'

/**
 * The page a Needs you email's "Review and decide" link opens (#1778), for someone who may not be signed in. It reads
 * the link once and shows the approval as its decision card, with Approve and Reject. Loading it decides nothing: a
 * decision is sent only when a button is pressed, and only once. The card then stays on the page, settled.
 *
 * A link that cannot decide says why, in the kit's banner: already decided (by whom and when), already used while its
 * approval still waits, expired, no longer yours to use, or not a link that works. Each offers Needs you, where
 * everything still waiting is, except the one the person may no longer decide in, which points to another owner.
 * Built from @zerohuman/ui alone, as the sign-in screen is.
 */

type Decision = 'approve' | 'reject'
type Refusal = Exclude<GateLinkView, { state: 'open' }>
type BannerTone = 'action' | 'system' | 'warning' | 'blocked'

/** Where everything still waiting is: the Needs you page, which asks a signed-out visitor to sign in first. */
const NEEDS_YOU_PATH = '/gates'

const route = useRoute()
const token = computed(() => {
    const raw = route.params['token']
    return typeof raw === 'string' ? raw : ''
})

const loading = ref(true)
const loadFailed = ref(false)
const gate = ref<GateLinkGate | null>(null)
const refusal = ref<Refusal | null>(null)
/** The decision on its way, so a second press (or the other button) sends nothing more. */
const sending = ref<Decision | null>(null)
const sendFailed = ref(false)
/** How the gate ended once this page decided it: the card keeps it for good. */
const settled = ref<SettledOutcome | undefined>(undefined)
const openedAt = ref(Date.now())

onMounted(async () => {
    try {
        const view = await gateLinkApi.view(token.value)
        if (view.state === 'open') {
            gate.value = view.gate
        } else {
            refusal.value = view
        }
    } catch {
        loadFailed.value = true
    } finally {
        openedAt.value = Date.now()
        loading.value = false
    }
})

/** A decided gate's status as the card settles on it. */
function settledOf(status: string): SettledOutcome {
    if (status === 'approved') return 'approve'
    if (status === 'rejected') return 'reject'
    if (status === 'changes_requested') return 'request_changes'
    return 'closed'
}

/** Sends the one decision this link can make. Every press after the first, of either button, sends nothing. */
async function decide(decision: Decision): Promise<void> {
    if (sending.value || settled.value || !gate.value) return
    sending.value = decision
    sendFailed.value = false
    try {
        const outcome = await gateLinkApi.decide(token.value, decision)
        if (outcome.state === 'decided') {
            settled.value = settledOf(outcome.gate.status)
        } else {
            // Decided elsewhere, expired or refused since the page opened: say so, and offer nothing to press.
            refusal.value = outcome
            gate.value = null
        }
    } catch {
        // Nothing came back, so nothing is known to be decided: the buttons stay, to try again.
        sendFailed.value = true
    } finally {
        sending.value = null
    }
}

/** The approval as its decision card draws it: what the run is for, who asks, how long it has waited, and why. */
const card = computed<DecisionView | null>(() => {
    const shown = gate.value
    if (!shown) return null
    const view: DecisionView = {
        title: shown.taskName || shown.taskSlug || toolLabel(shown.toolName, 'standard'),
        from: shown.askedBy?.name || 'Your team',
        waited: formatDuration(waitedMs(shown, openedAt.value)),
        sections: gateDecisionSections(shown),
    }
    if (shown.askedBy?.role) view.by = shown.askedBy.role
    return view
})

/** "11 Oct 2026, 10:30" in the reader's own time, or null when no time came back. */
function when(at: string | null): string | null {
    if (!at || Number.isNaN(Date.parse(at))) return null
    return new Date(at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

/** Who decided and when, in a sentence, for a gate that was already decided. */
function decidedSentence(decidedBy: string | null, decidedAt: string | null): string {
    const at = when(decidedAt)
    if (decidedBy && at) return `${decidedBy} decided it on ${at}. This link changed nothing.`
    if (decidedBy) return `${decidedBy} decided it. This link changed nothing.`
    if (at) return `It was decided on ${at}. This link changed nothing.`
    return 'It was decided before this link was used. This link changed nothing.'
}

/** What the page says instead of the card, when it cannot offer a decision. */
const notice = computed<{ banner: string; tone: BannerTone; detail: string; needsYou: boolean } | null>(() => {
    if (loadFailed.value) {
        return {
            banner: 'This page could not be opened',
            tone: 'blocked',
            detail: 'Nothing was decided. Check your connection and open the link again.',
            needsYou: true,
        }
    }
    const shown = refusal.value
    if (!shown) return null
    switch (shown.state) {
        case 'already_decided':
            return {
                banner: 'Already decided',
                tone: 'system',
                detail: decidedSentence(shown.decidedBy, shown.decidedAt),
                needsYou: true,
            }
        case 'used':
            return {
                banner: 'This link has already been used',
                tone: 'system',
                detail: 'Its decision has not come through, so this link changes nothing more. Open Needs you to see whether the approval is still waiting, and decide it there.',
                needsYou: true,
            }
        case 'expired':
            return {
                banner: 'This link has expired',
                tone: 'warning',
                detail: 'A link works for 72 hours after the email is sent, and only while its approval is still waiting. Nothing was decided. Anything still waiting is in Needs you.',
                needsYou: true,
            }
        case 'not_allowed':
            return {
                banner: 'This link is no longer yours to use',
                tone: 'blocked',
                detail: 'Only an owner of this company can use it, and only while the company is open. Nothing was decided. If the approval still needs a decision, ask another owner to decide it in Needs you.',
                needsYou: false,
            }
        default:
            return {
                banner: "This link doesn't work",
                tone: 'blocked',
                detail: 'It may have been cut short or changed. Nothing was decided. Open Needs you to decide there.',
                needsYou: true,
            }
    }
})

/** What the page says once the card is settled. */
const doneSentence = computed(() => {
    if (settled.value === 'approve') return 'Approved. The work carries on. You can close this page.'
    if (settled.value === 'reject') return 'Rejected. Nothing goes ahead. You can close this page.'
    return 'Decided. You can close this page.'
})
</script>

<template>
	<ZhAuthLayout>
		<template #form>
			<div class="flex w-full flex-col gap-6" data-testid="decide-page">
				<div class="flex flex-col gap-2">
					<ZhPageTitle>Review and decide</ZhPageTitle>
					<p class="text-muted-foreground">From your Needs you email. Opening this page decides nothing.</p>
				</div>

				<ZhSystemLabel v-if="loading" tone="system">Opening the link…</ZhSystemLabel>

				<template v-else-if="notice">
					<ZhBanner :tone="notice.tone" data-testid="decide-refusal">
						{{ notice.banner }}
						<template v-if="notice.needsYou" #action>
							<a :href="NEEDS_YOU_PATH">Open Needs you</a>
						</template>
					</ZhBanner>
					<p class="text-sm text-muted-foreground">{{ notice.detail }}</p>
					<p v-if="notice.needsYou" class="text-sm text-muted-foreground" data-testid="decide-sign-in">
						Needs you asks you to sign in first.
					</p>
				</template>

				<template v-else-if="card">
					<ZhDecisionCard :decision="card" :settled="settled" settled-when="just now">
						<template #actions>
							<Button type="button" size="sm" :disabled="sending !== null" @click="decide('approve')">
								{{ sending === 'approve' ? 'Approving…' : 'Approve' }}
							</Button>
							<Button
								type="button"
								size="sm"
								variant="destructive"
								:disabled="sending !== null"
								@click="decide('reject')"
							>
								{{ sending === 'reject' ? 'Rejecting…' : 'Reject' }}
							</Button>
						</template>
					</ZhDecisionCard>
					<p v-if="!settled" class="text-sm text-muted-foreground" data-testid="decide-final">
						Approve and Reject are final: once you press one, the decision cannot be undone.
					</p>
					<ZhStatusPill v-if="sendFailed" tone="failed">We could not confirm your decision. Try again, or check Needs you.</ZhStatusPill>
					<p v-if="settled" class="text-sm text-muted-foreground" data-testid="decide-done">{{ doneSentence }}</p>
				</template>
			</div>
		</template>
		<template #footer>
			<p class="text-sm text-muted-foreground">Each link works once, only for the owner it was emailed to, for up to 72 hours while its approval is waiting.</p>
		</template>
		<template #splash>
			<ZhPanel class="flex w-full">
				<div class="flex w-full flex-col justify-center gap-4 px-4 py-10 xl:px-10 xl:py-16">
					<ZhSystemLabel>How this link works</ZhSystemLabel>
					<p class="text-muted-foreground">
						Approve or Reject here decides it exactly as Needs you would, as you. The run then carries on or stops,
						and the link stops working. Anything else, such as asking for changes, is in Needs you.
					</p>
				</div>
			</ZhPanel>
		</template>
	</ZhAuthLayout>
</template>
